import { useQuery } from '@apollo/client';
import type { Patient, Practitioner } from '@medplum/fhirtypes';
import React, { useMemo, useState } from 'react';

import {
  flattenFhirGraphQLData,
  formatHumanName,
  useFhirQuery,
} from '@eluve/fhir-renderer';
import { PATIENT_RAIL_QUERY } from '@eluve/medplum-client';

import { EditPatientDialog } from './EditPatientDialog';
import {
  PatientProfileRailView,
  type RailFhirState,
} from './PatientProfileRailView';
import { PatientStatusConfirmDialog } from './PatientStatusConfirmDialog';
import { getPatientVisitDatesQuery } from './operations';
import { usePatientStatusActions } from './usePatientStatusActions';

export const PatientProfileRail: React.FC<{
  tenantId: string;
  patientId: string;
  identity: { name: string; initials: string };
}> = ({ tenantId, patientId, identity }) => {
  const { data, loading, error, enabled, refetch } = useFhirQuery(
    PATIENT_RAIL_QUERY,
    { variables: { identifier: patientId } },
  );
  const [editOpen, setEditOpen] = useState(false);
  const onSaved = () => void refetch();

  const { patient, primaryCareName } = useMemo(() => {
    const resources = flattenFhirGraphQLData(data ?? null);
    const flattened =
      resources.find(
        (resource): resource is Patient => resource.resourceType === 'Patient',
      ) ?? null;
    const generalPractitioner = flattened?.generalPractitioner?.[0];
    const resolved = resources.find(
      (resource): resource is Practitioner =>
        resource.resourceType === 'Practitioner' &&
        generalPractitioner?.reference === `Practitioner/${resource.id}`,
    );
    return {
      patient: flattened,
      primaryCareName:
        generalPractitioner?.display ??
        (formatHumanName(resolved?.name?.[0]) || undefined),
    };
  }, [data]);

  const fhir: RailFhirState = patient
    ? { status: 'data', patient }
    : !enabled || loading
      ? { status: 'loading' }
      : error
        ? { status: 'error' }
        : { status: 'unavailable' };

  const now = useMemo(() => new Date().toISOString(), []);
  const { data: visitsData } = useQuery(getPatientVisitDatesQuery, {
    variables: { tenantId, patientId, now },
  });
  const visits = {
    lastVisit: visitsData?.lastVisit?.aggregate?.max?.startDate,
    nextVisit: visitsData?.nextVisit?.aggregate?.min?.startDate,
  };

  const statusActions = usePatientStatusActions({
    tenantId,
    patientId,
    patient,
    onSaved,
  });

  return (
    <>
      <PatientProfileRailView
        identity={{ ...identity, photoUrl: patient?.photo?.[0]?.url }}
        fhir={fhir}
        primaryCareName={primaryCareName}
        visits={visits}
        onEditProfile={patient ? () => setEditOpen(true) : undefined}
        onStatusAction={patient ? statusActions.requestAction : undefined}
      />
      <PatientStatusConfirmDialog
        action={statusActions.confirmAction}
        patientName={identity.name}
        isPending={statusActions.isPending}
        onConfirm={statusActions.confirm}
        onCancel={statusActions.cancel}
      />
      {patient && (
        <EditPatientDialog
          patient={patient}
          open={editOpen}
          onOpenChange={setEditOpen}
          onSaved={onSaved}
        />
      )}
    </>
  );
};
